import { Button, Space } from 'antd';
import { useRef, useState, useEffect } from 'react';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import toilet from '../../images/general_toilet.png';
import canteen from '../../images/canteen.png';
import lift from '../../images/lift.png';
import student_hall from '../../images/student-hall.png';
import staff_quarter from '../../images/staff-quarter.png';
import transport from '../../images/transport.png';
import aed from '../../images/AED.png';
import useIsMobile from '../../helpers/customHooks/useIsMobile';

const PointOfInterestSelector = ({ onIconClick, className, loadingLocType }) => {
	const themeDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
	const isMobile = useIsMobile();
	const containerRef = useRef(null);
	// Scroll arrows are only shown on phones (hidden by CSS on desktop), sized to the chips.
	const navButtonSize = 28;
	const [showLeftButton, setShowLeftButton] = useState(false);
	const [showRightButton, setShowRightButton] = useState(true);
	const [pendingKeyword, setPendingKeyword] = useState(null);

	// Hardcoded shortcut icons with their behaviors
	const shortcutIcons = [
		{
			name: 'Lifts',
			icon: lift,
			keyword: 'Lift Shaft'
		},
		// {
		// 	name: 'Transport',
		// 	icon: transport,
		// 	keyword: null // TBC
		// },
		// {
		// 	name: 'Toilets',
		// 	icon: toilet,
		// 	keyword: 'Toilet'
		// },
		{
			name: 'Canteen',
			icon: canteen,
			keyword: 'Canteen'
		},
		{
			name: 'AED',
			icon: aed,
			keyword: 'AED'
		},
		{
			name: 'Staff Quarter',
			icon: staff_quarter,
			keyword: 'Staff Quarter'
		},
		{
			name: 'Student Hall',
			icon: student_hall,
			keyword: 'Student Hall'
		}
	];

	const checkScrollPosition = () => {
		if (containerRef.current) {
			const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
			setShowLeftButton(scrollLeft > 0);
			setShowRightButton(scrollLeft < scrollWidth - clientWidth);
		}
	};

	const scroll = (direction) => {
		if (containerRef.current) {
			const scrollAmount = 200;
			containerRef.current.scrollBy({
				left: direction === 'left' ? -scrollAmount : scrollAmount,
				behavior: 'smooth'
			});

			setTimeout(checkScrollPosition, 300);
		}
	};

	// Handle button click
	const handleButtonClick = (keyword) => {
		if (keyword) {
			if (!loadingLocType) {
				onIconClick(keyword);
			} else {
				setPendingKeyword(keyword);
			}
		}
	};

	// Execute pending keyword when loadingLocType becomes false
	useEffect(() => {
		if (!loadingLocType && pendingKeyword) {
			onIconClick(pendingKeyword);
			setPendingKeyword(null);
		}
	}, [loadingLocType, pendingKeyword, onIconClick]);

	useEffect(() => {
		const container = containerRef.current;
		if (container) {
			container.addEventListener('scroll', checkScrollPosition);
			window.addEventListener('resize', checkScrollPosition);

			// Initial check
			checkScrollPosition();

			return () => {
				container.removeEventListener('scroll', checkScrollPosition);
				window.removeEventListener('resize', checkScrollPosition);
			};
		}
	}, []);

	return (
		<div style={{ position: 'relative' }}>

			{showLeftButton && (
				<button
					className="poi-nav-button"
					onClick={() => scroll('left')}
					aria-label="Previous POI"
					style={{
						position: 'absolute',
						left: '0',
						top: '50%',
						transform: 'translateY(-50%)',
						zIndex: 100,
						borderRadius: navButtonSize / 2,
						height: navButtonSize,
						width: navButtonSize,
						backgroundColor: themeDark ? '#2c3036' : '#ffffff',
						border: 'none',
						boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
						cursor: 'pointer',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center'
					}}
				>
					<LeftOutlined style={{ color: themeDark ? '#EDEDED' : '#212121' }} />
				</button>
			)}

			<Space
				ref={containerRef}
				size={[8, 8]}
				className={className}
				wrap={false}
				style={{
					height: 'fit-content',
					overflowX: 'auto',
					scrollbarWidth: 'none',
					msOverflowStyle: 'none',
					padding: '0 8px',
					width: 'calc(100% - 16px)',
					margin: '0 auto'
				}}
			>
				{shortcutIcons.map(({ name, icon, keyword }, key) => (
					<Button
						key={key}
						size="large"
						shape="default"
						icon={
							icon ? (
								<img
									src={icon}
									alt={name}
									style={{ width: isMobile ? 16 : 20, height: isMobile ? 16 : 20, verticalAlign: 'middle' }}
								/>
							) : null
						}
						style={{
							boxShadow: '0 0px 24px -12px #000000AA',
							border: 'unset',
							color: themeDark ? '#FFFFFF' : '#003366',
							fontSize: isMobile ? 13 : 14,
							fontWeight: 510,
							flex: '0 0 auto',
							// Compact chips on phones leave more of the row, and the map, visible.
							...(isMobile && { height: 30, paddingInline: 10, gap: 6 })
						}}
						onClick={() => handleButtonClick(keyword)}
						disabled={!keyword}
					>
						{name}
					</Button>
				))}
			</Space>

			{showRightButton && (
				<button
					className="poi-nav-button"
					onClick={() => scroll('right')}
					aria-label="Next POI"
					style={{
						position: 'absolute',
						right: '0',
						top: '50%',
						transform: 'translateY(-50%)',
						zIndex: 100,
						borderRadius: navButtonSize / 2,
						height: navButtonSize,
						width: navButtonSize,
						backgroundColor: themeDark ? '#2c3036' : '#ffffff',
						border: 'none',
						boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
						cursor: 'pointer',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center'
					}}
				>
					<RightOutlined style={{ color: themeDark ? '#EDEDED' : '#212121' }} />
				</button>
			)}
		</div>
	);
};

export default PointOfInterestSelector;